import { api, throwApiError } from "../../../api/api";
import { ProductReference } from "./productReferenceApi";

export type MassBalanceStatus = "OPEN" | "RECOUNTED" | "CLOSED";
export type AdjustmentType = "AVARIA" | "VENCIMENTO" | "CONSUMO" | "DEVOLUCAO";
export type OccurrenceSource = "MANUAL" | "FILE";
export type AjusteEstoqueTipo =
  | "AVARIAS_NAO_APONTADAS"
  | "DEVOLUCAO_NAO_INTEGRADA"
  | "EM_ANALISE"
  | "ERRO_DE_CONTAGEM"
  | "OK"
  | "OUTRO_ERRO"
  | "PRIMEIRA_CONTAGEM"
  | "RECEBIMENTO_NAO_APONTADO"
  | "RECEBIMENTO_NAO_INTEGRADO"
  | "SINISTRO"
  | "VENCIMENTO_NAO_APONTADO"
  | "VENDAS_DIVERGENTES";

// Ordem de exibição do dropdown de justificativa do ajuste de estoque
export const AJUSTE_TIPO_LABELS: Record<AjusteEstoqueTipo, string> = {
  AVARIAS_NAO_APONTADAS: "Avarias não apontadas",
  DEVOLUCAO_NAO_INTEGRADA: "Devolução não integrada",
  EM_ANALISE: "Em análise",
  ERRO_DE_CONTAGEM: "Erro de contagem",
  OK: "OK",
  OUTRO_ERRO: "Outro erro (Especificar)",
  PRIMEIRA_CONTAGEM: "Primeira contagem",
  RECEBIMENTO_NAO_APONTADO: "Recebimento não apontado",
  RECEBIMENTO_NAO_INTEGRADO: "Recebimento não integrado",
  SINISTRO: "Sinistro",
  VENCIMENTO_NAO_APONTADO: "Vencimento não apontado",
  VENDAS_DIVERGENTES: "Vendas divergentes",
};

export type StoreShort = {
  id: string;
  nomeFantasia: string;
  endereco?: string | null;
  estado?: string | null;
};

export type MassBalanceItem = {
  id: string;
  massBalanceId: string;
  sku: string;
  descricao: string;
  valor: number;
  isTracked: boolean;
  contagemAnterior: number;
  entrega: number;
  venda: number;
  devolucao: number;
  avarias: number;
  vencimentos: number;
  consumos: number;
  projetado: number;
  contagem?: number | null;
  contagemSetAt?: string | null;
  variacaoContagem?: number | null;
  tt?: number | null;
  recontagem?: number | null;
  recontagemSetAt?: string | null;
  variacaoRecontagem?: number | null;
  ttRecontagem?: number | null;
  ajusteEstoque?: number | null;
  ajusteEstoqueSetAt?: string | null;
  ajusteProjetadaAnterior?: number | null;
  ajusteVariacao?: number | null;
  ajusteTt?: number | null;
  ajusteJustificativaTexto?: string | null;
  ajusteJustificativaTipo?: AjusteEstoqueTipo | null;
  saidasResolvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type BalanceOccurrence = {
  id: string;
  massBalanceId: string;
  massBalanceItemId?: string | null;
  productRefId?: string | null;
  productRef?: ProductReference | null;
  codAdmin?: string | null;
  type: AdjustmentType;
  source: OccurrenceSource;
  quantity: number;
  observacao?: string | null;
  nomeMotivo?: string | null;
  createdAt: string;
};

export type XmlUnmatchedEntry = {
  id: string;
  massBalanceId: string;
  sku: string;
  descricao: string;
  entrega: number;
  valor: number;
  isLikelyPack: boolean;
  detectedPackSize?: number | null;
  createdAt: string;
};

export type SaidaUnmatchedEntry = {
  id: string;
  massBalanceId: string;
  codAdmin?: string | null;
  descricao: string;
  qty: number;
  isLikelyPack: boolean;
  detectedPackSize?: number | null;
  createdAt: string;
};

export type MassBalance = {
  id: string;
  storeId: string;
  store: StoreShort;
  status: MassBalanceStatus;
  createdById: string;
  createdBy: { nomeCompleto: string };
  referenceDate: string;
  entradasUploadedAt?: string | null;
  saidasUploadedAt?: string | null;
  ocorrenciasUploadedAt?: string | null;
  ocorrenciaUnmatchedCount: number;
  ocorrenciaUnmatchedDismissedAt?: string | null;
  saidaErrosDePara?: any;
  saidaErrosDismissedAt?: string | null;
  items: MassBalanceItem[];
  occurrences: BalanceOccurrence[];
  xmlUnmatchedEntries: XmlUnmatchedEntry[];
  saidaUnmatchedEntries: SaidaUnmatchedEntry[];
  createdAt: string;
  updatedAt: string;
};

export type StoreProductStock = {
  storeId: string;
  productReferenceId: string;
  productReference: ProductReference;
  estoqueProjetado: number;
};

export type StoreProductTracking = {
  storeId: string;
  productReferenceId: string;
  productReference: ProductReference;
  isTracked: boolean;
};

export type DeficitNotification = {
  id: string;
  massBalanceId: string;
  massBalance: { referenceDate: string; store: StoreShort };
  storeId: string;
  store: StoreShort;
  itemCount: number;
  dismissedAt?: string | null;
  createdAt: string;
};

export type DuplicateDecision = {
  codAdmin: string;
  decision: "increment" | "replace" | "ignore";
};

export type TeachPackDto = {
  packEan: string;
  descricao?: string;
  deliveryQty: number;
  baseProductId: string;
  packSize: number;
  numeroItem?: string;
  valorPorPack?: number;
};

export type TeachSaidaPackDto = {
  packCodAdmin: string;
  descricao?: string;
  saidaQty: number;
  baseCodAdmin: string;
  packSize: number;
  valorPorPack?: number;
};

export type TeachOcorrenciaDto = {
  codAdmin: string;
  mode: "link" | "create";
  productReferenceId?: string;
  ean?: string;
  descricao?: string;
};

export type UpdateItemDto = {
  contagem?: number;
  recontagem?: number;
  ajusteEstoque?: number;
  ajusteJustificativaTexto?: string;
  ajusteJustificativaTipo?: AjusteEstoqueTipo;
};

export type ManualOccurrenceDto = {
  productReferenceId?: string;
  codAdmin?: string;
  type: AdjustmentType;
  quantity: number;
  observacao?: string;
};

export const getMassBalanceStoresApi = async (token: string): Promise<StoreShort[]> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get<StoreShort[]>("/mass-balance/stores", {
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar lojas do balanço.");
    throw error;
  }
};

export type StoreTrackingReference = ProductReference & {
  isTracked: boolean;
};

export type PaginatedStoreTrackingResponse = {
  data: StoreTrackingReference[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

export const getStoreTrackingApi = async (
  token: string,
  storeId: string,
  params: { query?: string; page?: number; limit?: number } = {}
): Promise<PaginatedStoreTrackingResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get<PaginatedStoreTrackingResponse>(
      `/mass-balance/stores/${storeId}/tracking`,
      {
        params,
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar rastreamento da loja.");
    throw error;
  }
};

export const setStoreTrackingApi = async (
  token: string,
  storeId: string,
  productReferenceId: string,
  isTracked: boolean
): Promise<any> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.patch<any>(
      `/mass-balance/stores/${storeId}/tracking/${productReferenceId}`,
      { isTracked },
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar configuração de rastreamento.");
    throw error;
  }
};

export const getStoreStockListApi = async (
  token: string,
  storeId: string,
  params: { query?: string; page?: number; limit?: number } = {}
): Promise<{ data: StoreProductStock[]; meta: any }> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get<{ data: StoreProductStock[]; meta: any }>(
      `/mass-balance/stores/${storeId}/stock`,
      {
        params,
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar estoque da loja.");
    throw error;
  }
};

export const adjustStoreStockApi = async (
  token: string,
  storeId: string,
  productReferenceId: string,
  body: { newValue: number; reason: string }
): Promise<any> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.patch(
      `/mass-balance/stores/${storeId}/stock/${productReferenceId}`,
      body,
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao corrigir estoque atual.");
    throw error;
  }
};

export const getCurrentBalanceApi = async (
  token: string,
  storeId: string
): Promise<MassBalance | null> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get<MassBalance>(`/mass-balance/${storeId}/current`, {
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    // If no active balance exists, it can return 404/null
    return null;
  }
};

export const openBalanceApi = async (
  token: string,
  storeId: string
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/stores/${storeId}/open`,
      {},
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao abrir balanço.");
    throw error;
  }
};

export const uploadFirstCountApi = async (
  token: string,
  storeId: string,
  file: File
): Promise<{ message: string; balance: MassBalance }> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("storeId", storeId);
  try {
    const { data } = await api.post<{ message: string; balance: MassBalance }>(
      "/mass-balance/upload",
      formData,
      {
        headers: {
          Authorization: `Bearer ${rawToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro no upload do XML de Entradas.");
    throw error;
  }
};

export const uploadSaidasApi = async (
  token: string,
  storeId: string,
  file: File
): Promise<{ message: string; balance: MassBalance }> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("storeId", storeId);
  try {
    const { data } = await api.post<{ message: string; balance: MassBalance }>(
      "/mass-balance/upload-saidas",
      formData,
      {
        headers: {
          Authorization: `Bearer ${rawToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro no upload da planilha de Saídas.");
    throw error;
  }
};

export const uploadOcorrenciasApi = async (
  token: string,
  storeId: string,
  file: File
): Promise<{ message: string; balance: MassBalance; unmatchedTypes?: string[] }> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("storeId", storeId);
  try {
    const { data } = await api.post<{ message: string; balance: MassBalance; unmatchedTypes?: string[] }>(
      "/mass-balance/upload-ocorrencias",
      formData,
      {
        headers: {
          Authorization: `Bearer ${rawToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro no upload de planilha de Ocorrências.");
    throw error;
  }
};

export const addManualOccurrenceApi = async (
  token: string,
  storeId: string,
  dto: ManualOccurrenceDto
): Promise<{ message: string; balance: MassBalance }> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<{ message: string; balance: MassBalance }>(
      `/mass-balance/stores/${storeId}/occurrences`,
      dto,
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao registrar ocorrência manual.");
    throw error;
  }
};

export const resolveDuplicatesApi = async (
  token: string,
  balanceId: string,
  decisions: DuplicateDecision[]
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/resolve-duplicates`,
      { decisions },
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao resolver duplicidades de saídas.");
    throw error;
  }
};

export const updateMassBalanceItemApi = async (
  token: string,
  balanceId: string,
  itemId: string,
  dto: UpdateItemDto
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.patch<MassBalance>(
      `/mass-balance/${balanceId}/items/${itemId}`,
      dto,
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar item do balanço.");
    throw error;
  }
};

export const setItemTrackedApi = async (
  token: string,
  balanceId: string,
  itemId: string,
  isTracked: boolean
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.patch<MassBalance>(
      `/mass-balance/${balanceId}/items/${itemId}/track`,
      { isTracked },
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar rastreamento do item.");
    throw error;
  }
};

export const teachPackApi = async (
  token: string,
  balanceId: string,
  dto: TeachPackDto
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/teach-pack`,
      dto,
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao ensinar pack (XML).");
    throw error;
  }
};

export const teachSaidaPackApi = async (
  token: string,
  balanceId: string,
  dto: TeachSaidaPackDto
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/teach-saida-pack`,
      dto,
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao ensinar pack (Saídas).");
    throw error;
  }
};

export const teachOcorrenciaApi = async (
  token: string,
  balanceId: string,
  dto: TeachOcorrenciaDto
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/ocorrencia-unmatched/teach`,
      dto,
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao vincular De/Para da ocorrência.");
    throw error;
  }
};

export const unteachPackApi = async (
  token: string,
  balanceId: string,
  packEan: string
): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/unteach-pack`,
      { packEan },
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao desvincular pack.");
    throw error;
  }
};

export const dismissXmlUnmatchedApi = async (token: string, balanceId: string): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/xml-unmatched/dismiss`,
      {},
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao dispensar pendências de XML.");
    throw error;
  }
};

export const dismissSaidaUnmatchedApi = async (token: string, balanceId: string): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/saida-unmatched/dismiss`,
      {},
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao dispensar pendências de saídas multipack.");
    throw error;
  }
};

export const dismissSaidaErrosApi = async (token: string, balanceId: string): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/saida-erros/dismiss`,
      {},
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao dispensar erros de saídas.");
    throw error;
  }
};

export const dismissOcorrenciaUnmatchedApi = async (token: string, balanceId: string): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.post<MassBalance>(
      `/mass-balance/${balanceId}/ocorrencia-unmatched/dismiss`,
      {},
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao dispensar pendências de ocorrências.");
    throw error;
  }
};

export const closeMassBalanceApi = async (token: string, id: string): Promise<MassBalance> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.patch<MassBalance>(
      `/mass-balance/${id}/close`,
      {},
      { headers: { Authorization: `Bearer ${rawToken}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao encerrar balanço.");
    throw error;
  }
};

export const getDeficitNotificationsApi = async (
  token: string,
  all = false
): Promise<DeficitNotification[]> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get<DeficitNotification[]>("/mass-balance/deficit-notifications", {
      params: { all },
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar notificações de déficit.");
    throw error;
  }
};

export const getDeficitReportExcelApi = async (
  token: string,
  massBalanceId: string
): Promise<Blob> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get(`/mass-balance/deficit-notifications/${massBalanceId}/excel`, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao baixar Excel de déficit.");
    throw error;
  }
};

export const getMassBalanceReportApi = async (
  token: string,
  params: { storeIds?: string[]; startDate?: string; endDate?: string; search?: string; status?: string[] } = {}
): Promise<any[]> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get<any[]>("/mass-balance/report", {
      params,
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar relatório de balanços.");
    throw error;
  }
};

export const getMassBalanceReportExcelApi = async (
  token: string,
  params: { storeIds?: string[]; startDate?: string; endDate?: string; search?: string; status?: string[] } = {}
): Promise<Blob> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get("/mass-balance/report/excel", {
      params,
      responseType: "blob",
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao baixar relatório em Excel.");
    throw error;
  }
};

export const getEntradasSaidasReportApi = async (
  token: string,
  params: { storeIds?: string[]; estados?: string[]; startDate?: string; endDate?: string; search?: string } = {}
): Promise<any[]> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get<any[]>("/mass-balance/entradas-saidas", {
      params,
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar relatório de entradas/saídas.");
    throw error;
  }
};

export const getEntradasSaidasReportExcelApi = async (
  token: string,
  params: { storeIds?: string[]; estados?: string[]; startDate?: string; endDate?: string; search?: string } = {}
): Promise<Blob> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  try {
    const { data } = await api.get("/mass-balance/entradas-saidas/excel", {
      params,
      responseType: "blob",
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao baixar Excel de entradas/saídas.");
    throw error;
  }
};


